import { useState } from 'react';
import { IconCircleOff, IconDeviceFloppy, IconLock, IconMinus, IconPlus, IconSearch, IconSend } from '@tabler/icons-react';
import { Button, Drawer, Input, Select, useToast } from '@hana/ui';
import { INV_CUS } from './inv';
import { DR_CHI_HO, DR_FX_JOB, DR_ROW, drChecks, drDefaultMst, drLines, drPeriodLocked, drTotals } from './invdr';
import s from './invdr.module.css';
import { useT } from '@hana/i18n';
import './invdr.i18n';

// Mint-Invoice › DRAWER "Lập hoá đơn" (640px). TIỀN → chỉ mở từ màn có quyền `money`. Chỉ đọc: Lưu nháp / Phát hành báo toast "Chưa mở ghi ở bản này".
const vn = (x: number): string => Math.round(x).toLocaleString('vi-VN');

export function InvDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  return open ? <Inner onClose={onClose} /> : null;
}

function Inner({ onClose }: { onClose: () => void }) {
  const tr = useT();
  const d = useDr();
  return <Drawer open onOpenChange={(o) => !o && onClose()} title={tr('dr.title')} footer={<Foot d={d} onClose={onClose} />}><Body d={d} /></Drawer>;
}

function useDr() {
  const cus = DR_ROW.cus;
  const [mst, setMst] = useState(drDefaultMst(cus));
  const [looked, setLooked] = useState(false);
  const [addr, setAddr] = useState<string | undefined>();
  const [email, setEmail] = useState<string | undefined>();
  const [fx, setFx] = useState(DR_FX_JOB);
  const [sign, setSign] = useState(true);
  const [mail, setMail] = useState(true);
  const [tried, setTried] = useState(false);
  const r = drChecks({ cus, mst, looked, addr, email, periodLocked: drPeriodLocked() });
  return { cus, mst, setMst, looked, setLooked, addr, setAddr, email, setEmail, fx, setFx, sign, setSign, mail, setMail, tried, setTried, r };
}
type Dr = ReturnType<typeof useDr>;

function Body({ d }: { d: Dr }) {
  const tr = useT();
  const toast = useToast();
  const { r, cus, fx } = d;
  const low = fx < DR_FX_JOB;
  const miss = r.checks.filter((c) => !c.ok);
  return (
    <div className={s.dr}>
      <span className={s.pill}>{tr('dr.fromDebit', { dn: DR_ROW.dn })}</span>
      <section aria-label={tr('dr.buyerAria')}>
        <b className={s.h}>{tr('dr.buyer')}</b>
        <div className={s.grid}>
          <div>
            <div className={s.mstRow}>
              <Input label={tr('dr.mst')} value={d.mst} onChange={(e) => { d.setMst(e.target.value); d.setLooked(false); d.setTried(false); }} />
              <Button icon={<IconSearch size={16} aria-hidden="true" />} onClick={() => { d.setLooked(true); d.setMst(INV_CUS[cus]![1]); toast({ msg: tr('dr.looked', { mst: INV_CUS[cus]![1] }) }); }}>{tr('dr.lookup')}</Button>
            </div>
            <div className={r.mstOk ? s.ok : s.lb}>{r.mstOk ? tr('dr.mstOk') : tr('dr.mstHint')}</div>
          </div>
          <Input label={tr('dr.name')} value={r.name} readOnly />
          <div className={s.span2}><Input label={tr('dr.addr')} value={r.addr} placeholder={tr('dr.addrPh')} error={d.tried && !r.addr.trim() ? tr('dr.addrMiss') : undefined} onChange={(e) => d.setAddr(e.target.value)} /></div>
          <Input label={tr('dr.email')} type="email" value={r.email} placeholder="ketoan@khach.vn" error={d.tried && !r.checks[4]!.ok ? tr('dr.emailBad') : undefined} onChange={(e) => d.setEmail(e.target.value)} />
          <Select label={tr('dr.pay')} options={[tr('dr.payTf'), 'TM/CK']} />
        </div>
      </section>
      <section aria-label={tr('dr.linesAria')} className={s.sec}>
        <div className={s.between}><b className={s.h}>{tr('dr.lines', { job: DR_ROW.job })}</b><span className={s.lb}>{tr('dr.vatNote')}</span></div>
        <div className={s.tbl}>
          <div className={`${s.tr} ${s.th}`}><span>{tr('dr.thCode')}</span><span>{tr('dr.thDesc')}</span><span className={s.r}>{tr('dr.thQty')}</span><span className={s.r}>{tr('dr.thPrice')}</span><span className={s.r}>VAT</span><span className={s.r}>{tr('dr.thAmt')}</span></div>
          {drLines(fx).map(([code, k, q, p, vat, v]) => (
            <div key={code} className={s.tr}>
              <b>{code}</b><span className={s.cut}>{k}</span><span className={s.r}>{q}</span><span className={s.r}>{p}</span>
              <span className={`${s.vat}`} title={tr('dr.vatTitle', { code })}><IconLock size={11} aria-hidden="true" />{vat}</span>
              <span className={s.r}>{vn(v)}</span>
            </div>
          ))}
        </div>
        <div className={s.warnBox}>{tr('dr.of0')}</div>
        <div className={`${s.lb} ${s.chH}`}>{tr('dr.chiHo')}</div>
        <div className={s.chRow}><span>{tr('dr.chiHoRow')}</span><b>{vn(DR_CHI_HO)}</b></div>
      </section>
      <section aria-label={tr('dr.fxAria')} className={s.sec}>
        <b className={s.h}>{tr('dr.fx')}</b>
        <div className={s.fxRow}>
          <div className={`${s.fx} ${low ? s.fxLow : ''}`}>
            <button type="button" aria-label={tr('dr.fxDown')} onClick={() => d.setFx(fx - 10)}><IconMinus size={16} aria-hidden="true" /></button>
            <span>{fx.toLocaleString('vi-VN')}</span>
            <button type="button" aria-label={tr('dr.fxUp')} onClick={() => d.setFx(fx + 10)}><IconPlus size={16} aria-hidden="true" /></button>
          </div>
          <span className={low ? s.warn : s.lb}>{low ? tr('dr.fxLow') : tr('dr.fxNote')}</span>
        </div>
      </section>
      <section aria-label={tr('dr.issueAria')} className={`${s.sec} ${s.col}`}>
        <b className={s.h}>{tr('dr.preCheck')}</b>
        <div className={s.checks}>
          {r.checks.map((c) => (
            <div key={c.k} className={s.chk}><span className={c.ok ? s.mOk : s.mErr} aria-hidden="true">{c.ok ? '✓' : '!'}</span><span className={s.grow}>{c.k}</span><span className={c.ok ? s.lb : s.err}>{c.v}</span></div>
          ))}
        </div>
        {d.tried && miss.length > 0 && <div role="alert" className={s.errBox}>{tr('dr.missing', { list: miss.map((c) => c.k).join(' · ') })}</div>}
        <b className={s.h}>{tr('dr.issueAria')}</b>
        <label className={s.chkL}><input type="checkbox" checked={d.sign} onChange={() => d.setSign(!d.sign)} />{tr('dr.sign')}</label>
        <label className={s.chkL}><input type="checkbox" checked={d.mail} onChange={() => d.setMail(!d.mail)} />{tr('dr.mail')}</label>
      </section>
    </div>
  );
}

function Foot({ d, onClose }: { d: Dr; onClose: () => void }) {
  const tr = useT();
  const toast = useToast();
  const fx = d.fx;
  const miss = d.r.checks.filter((c) => !c.ok).length;
  const t = drTotals(fx);
  const low = fx < DR_FX_JOB;
  return (
    <>
      <div className={s.tot}><div className={s.lb}>{tr('dr.total')}</div><b>{vn(t.total)} ₫</b></div>
      <Button variant="ghost" icon={<IconCircleOff size={16} aria-hidden="true" />} onClick={onClose}>{tr('dr.cancel')}</Button>
      <Button icon={<IconDeviceFloppy size={16} aria-hidden="true" />} onClick={() => { onClose(); toast({ msg: tr('dr.noWrite') }); }}>{tr('dr.draft')}</Button>
      <Button variant="primary" icon={<IconSend size={16} aria-hidden="true" />} title={miss ? tr('dr.missN', { n: miss }) : undefined} onClick={() => { if (miss) { d.setTried(true); return; } toast({ msg: tr('dr.noWrite') }); }}>{low ? tr('dr.sendKtt') : !d.sign ? tr('dr.issueAria') : tr('dr.issueSign')}</Button>
    </>
  );
}
